import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, FormControl, InputLabel, Select, MenuItem, TextField,
  CircularProgress, Divider, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Alert, Tooltip, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Send as SendIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Chat as LineIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as CartIcon,
  LocalOffer as OfferIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Preview as PreviewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { toast } from 'react-toastify';
import api from '../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AdminRemarketing = () => {
  const { t } = useTranslation('admin');

  const CAMPAIGN_TYPES = {
    abandoned_cart: { label: t('remarketing.type_abandoned_cart'), color: 'error', icon: <CartIcon /> },
    win_back: { label: t('remarketing.type_win_back'), color: 'warning', icon: <PeopleIcon /> },
    promotion: { label: t('remarketing.type_promotion'), color: 'success', icon: <OfferIcon /> },
    new_product: { label: t('remarketing.type_new_product'), color: 'primary', icon: <CampaignIcon /> },
    holiday: { label: t('remarketing.type_holiday'), color: 'secondary', icon: <ScheduleIcon /> }
  };

  const CHANNEL_CONFIG = {
    sms: { label: t('remarketing.channel_sms'), icon: <SmsIcon />, color: '#4caf50' },
    email: { label: t('remarketing.channel_email'), icon: <EmailIcon />, color: '#2196f3' },
    line: { label: t('remarketing.channel_line'), icon: <LineIcon />, color: '#00b900' }
  };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  // Dashboard data
  const [dashboard, setDashboard] = useState({
    total_campaigns: 0,
    active_campaigns: 0,
    total_sends_today: 0,
    total_sends_month: 0,
    open_rate: 0,
    click_rate: 0,
    conversion_rate: 0,
    channel_stats: []
  });

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState('all');

  // Templates
  const [templates, setTemplates] = useState([]);

  // Audience
  const [audience, setAudience] = useState({ items: [], stats: {} });
  const [audienceType, setAudienceType] = useState('abandoned_cart');

  // Reports
  const [reports, setReports] = useState({ campaigns: [], channels: [] });
  const [reportDays, setReportDays] = useState(30);

  // Dialog states
  const [campaignDialog, setCampaignDialog] = useState({ open: false, data: null, mode: 'create' });
  const [previewDialog, setPreviewDialog] = useState({ open: false, content: '' });
  const [sendDialog, setSendDialog] = useState({ open: false, campaign: null, sending: false });

  // Fetch all data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/remarketing/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Fetch dashboard failed:', error);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const params = campaignFilter !== 'all' ? `?status=${campaignFilter}` : '';
      const response = await api.get(`/remarketing/campaigns${params}`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Fetch campaigns failed:', error);
    }
  }, [campaignFilter]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/remarketing/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Fetch templates failed:', error);
    }
  }, []);

  const fetchAudience = useCallback(async () => {
    try {
      const response = await api.get(`/remarketing/audience?type=${audienceType}`);
      setAudience(response.data);
    } catch (error) {
      console.error('Fetch audience failed:', error);
    }
  }, [audienceType]);

  const fetchReports = useCallback(async () => {
    try {
      const [campaignRes, channelRes] = await Promise.all([
        api.get(`/remarketing/reports/campaigns?days=${reportDays}`),
        api.get(`/remarketing/reports/channels?days=${reportDays}`)
      ]);
      setReports({
        campaigns: campaignRes.data,
        channels: channelRes.data
      });
    } catch (error) {
      console.error('Fetch reports failed:', error);
    }
  }, [reportDays]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchCampaigns(), fetchTemplates()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchCampaigns, fetchTemplates]);

  useEffect(() => {
    if (tab === 2) fetchAudience();
    if (tab === 3) fetchReports();
  }, [tab, fetchAudience, fetchReports]);

  useEffect(() => {
    fetchCampaigns();
  }, [campaignFilter, fetchCampaigns]);

  useEffect(() => {
    fetchAudience();
  }, [audienceType, fetchAudience]);

  useEffect(() => {
    fetchReports();
  }, [reportDays, fetchReports]);

  // Campaign operations
  const handleCreateCampaign = () => {
    setCampaignDialog({
      open: true,
      mode: 'create',
      data: {
        name: '',
        type: 'promotion',
        channels: ['email'],
        template_id: '',
        is_automated: false,
        trigger_conditions: {},
        audience_filters: {},
        status: 'draft'
      }
    });
  };

  const handleEditCampaign = (campaign) => {
    setCampaignDialog({
      open: true,
      mode: 'edit',
      data: { ...campaign }
    });
  };

  const handleSaveCampaign = async () => {
    try {
      const { mode, data } = campaignDialog;
      if (mode === 'create') {
        await api.post('/remarketing/campaigns', data);
        toast.success(t('remarketing.campaign_created'));
      } else {
        await api.put(`/remarketing/campaigns/${data.id}`, data);
        toast.success(t('remarketing.campaign_updated'));
      }
      setCampaignDialog({ open: false, data: null, mode: 'create' });
      fetchCampaigns();
      fetchDashboard();
    } catch (error) {
      toast.error(t('remarketing.operation_failed') + ': ' + (error.response?.data?.message || error.message));
    }
  };

  const handleToggleCampaign = async (campaign) => {
    try {
      await api.post(`/remarketing/campaigns/${campaign.id}/toggle`);
      toast.success(campaign.status === 'active' ? t('remarketing.campaign_paused') : t('remarketing.campaign_activated'));
      fetchCampaigns();
      fetchDashboard();
    } catch (error) {
      toast.error(t('remarketing.operation_failed'));
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm(t('remarketing.confirm_delete'))) return;
    try {
      await api.delete(`/remarketing/campaigns/${campaignId}`);
      toast.success(t('remarketing.campaign_deleted'));
      fetchCampaigns();
      fetchDashboard();
    } catch (error) {
      toast.error(t('remarketing.delete_failed'));
    }
  };

  const handleSendCampaign = async () => {
    if (!sendDialog.campaign) return;
    setSendDialog(prev => ({ ...prev, sending: true }));
    try {
      const response = await api.post(`/remarketing/campaigns/${sendDialog.campaign.id}/send`);
      toast.success(t('remarketing.send_success', { count: response.data.sent_count }));
      setSendDialog({ open: false, campaign: null, sending: false });
      fetchCampaigns();
      fetchDashboard();
    } catch (error) {
      toast.error(t('remarketing.send_failed') + ': ' + (error.response?.data?.message || error.message));
      setSendDialog(prev => ({ ...prev, sending: false }));
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-TW');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-TW');
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon color="primary" /> {t('remarketing.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCampaign}
          >
            {t('remarketing.new_campaign')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchDashboard();
              fetchCampaigns();
            }}
          >
            {t('remarketing.refresh')}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Dashboard Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <CampaignIcon sx={{ fontSize: 36, color: '#1976d2' }} />
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {dashboard.total_campaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t('remarketing.total_campaigns')}</Typography>
                  <Chip size="small" label={t('remarketing.active_count', { count: dashboard.active_campaigns })} color="success" sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <SendIcon sx={{ fontSize: 36, color: '#388e3c' }} />
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {dashboard.total_sends_today}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t('remarketing.today_sends')}</Typography>
                  <Chip size="small" label={t('remarketing.monthly_count', { count: dashboard.total_sends_month })} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 36, color: '#f57c00' }} />
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {formatPercent(dashboard.open_rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t('remarketing.open_rate')}</Typography>
                  <Chip size="small" label={t('remarketing.click_count', { rate: formatPercent(dashboard.click_rate) })} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#fce4ec' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <OfferIcon sx={{ fontSize: 36, color: '#c2185b' }} />
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {formatPercent(dashboard.conversion_rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{t('remarketing.conversion_rate')}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Channel Stats */}
          {dashboard.channel_stats?.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('remarketing.channel_stats')}
              </Typography>
              <Grid container spacing={2}>
                {dashboard.channel_stats.map((stat) => (
                  <Grid item xs={4} key={stat.channel}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      borderLeft: `4px solid ${CHANNEL_CONFIG[stat.channel]?.color || '#999'}`
                    }}>
                      {CHANNEL_CONFIG[stat.channel]?.icon}
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {CHANNEL_CONFIG[stat.channel]?.label || stat.channel}
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {stat.total_sent}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab icon={<CampaignIcon />} label={t('remarketing.tab_campaigns')} />
              <Tab icon={<CopyIcon />} label={t('remarketing.tab_templates')} />
              <Tab icon={<PeopleIcon />} label={t('remarketing.tab_audience')} />
              <Tab icon={<AssessmentIcon />} label={t('remarketing.tab_reports')} />
            </Tabs>
          </Paper>

          {/* Tab 0: Campaign Management */}
          {tab === 0 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('remarketing.campaign_list')}</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>{t('remarketing.filter_status')}</InputLabel>
                  <Select
                    value={campaignFilter}
                    label={t('remarketing.filter_status')}
                    onChange={(e) => setCampaignFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('remarketing.status_all')}</MenuItem>
                    <MenuItem value="active">{t('remarketing.status_active')}</MenuItem>
                    <MenuItem value="paused">{t('remarketing.status_paused')}</MenuItem>
                    <MenuItem value="draft">{t('remarketing.status_draft')}</MenuItem>
                    <MenuItem value="completed">{t('remarketing.status_completed')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>{t('remarketing.col_campaign_name')}</TableCell>
                      <TableCell>{t('remarketing.col_type')}</TableCell>
                      <TableCell>{t('remarketing.col_channel')}</TableCell>
                      <TableCell align="center">{t('remarketing.col_status')}</TableCell>
                      <TableCell align="right">{t('remarketing.col_sent')}</TableCell>
                      <TableCell align="right">{t('remarketing.col_open_rate')}</TableCell>
                      <TableCell align="right">{t('remarketing.col_conversion')}</TableCell>
                      <TableCell>{t('remarketing.col_last_sent')}</TableCell>
                      <TableCell align="center">{t('remarketing.col_actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {campaign.is_automated && (
                              <Tooltip title={t('remarketing.automated_campaign')}>
                                <ScheduleIcon fontSize="small" color="primary" />
                              </Tooltip>
                            )}
                            <Typography fontWeight="medium">{campaign.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={CAMPAIGN_TYPES[campaign.type]?.icon}
                            label={CAMPAIGN_TYPES[campaign.type]?.label || campaign.type}
                            color={CAMPAIGN_TYPES[campaign.type]?.color || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {campaign.channels?.map((ch) => (
                              <Tooltip key={ch} title={CHANNEL_CONFIG[ch]?.label}>
                                <Box sx={{ color: CHANNEL_CONFIG[ch]?.color }}>
                                  {CHANNEL_CONFIG[ch]?.icon}
                                </Box>
                              </Tooltip>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              campaign.status === 'active' ? t('remarketing.status_active') :
                              campaign.status === 'paused' ? t('remarketing.status_paused') :
                              campaign.status === 'draft' ? t('remarketing.status_draft') : t('remarketing.status_completed')
                            }
                            color={
                              campaign.status === 'active' ? 'success' :
                              campaign.status === 'paused' ? 'warning' :
                              campaign.status === 'draft' ? 'default' : 'info'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">{campaign.total_sent || 0}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {campaign.total_sent > 0 ? (
                            <Typography>
                              {((campaign.total_opened / campaign.total_sent) * 100).toFixed(1)}%
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="success.main" fontWeight="bold">
                            {campaign.total_converted || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDateTime(campaign.last_sent_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={campaign.status === 'active' ? t('remarketing.pause') : t('remarketing.activate')}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleCampaign(campaign)}
                                color={campaign.status === 'active' ? 'warning' : 'success'}
                              >
                                {campaign.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('remarketing.send_now')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setSendDialog({ open: true, campaign, sending: false })}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('remarketing.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditCampaign(campaign)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('remarketing.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteCampaign(campaign.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {campaigns.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">{t('remarketing.no_campaigns')}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tab 1: Templates */}
          {tab === 1 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('remarketing.template_management')}</Typography>
                <Button variant="outlined" startIcon={<AddIcon />}>
                  {t('remarketing.new_template')}
                </Button>
              </Box>

              <Grid container spacing={2}>
                {templates.map((template) => (
                  <Grid item xs={12} md={6} lg={4} key={template.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {template.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip
                              size="small"
                              label={CAMPAIGN_TYPES[template.type]?.label || template.type}
                              color={CAMPAIGN_TYPES[template.type]?.color || 'default'}
                            />
                            <Chip
                              size="small"
                              icon={CHANNEL_CONFIG[template.channel]?.icon}
                              label={CHANNEL_CONFIG[template.channel]?.label}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {template.subject || t('remarketing.no_subject')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            bgcolor: '#f5f5f5',
                            p: 1,
                            borderRadius: 1,
                            maxHeight: 80,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {template.content?.substring(0, 100)}...
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                          <IconButton size="small" onClick={() => setPreviewDialog({ open: true, content: template.content })}>
                            <PreviewIcon />
                          </IconButton>
                          <IconButton size="small">
                            <CopyIcon />
                          </IconButton>
                          <IconButton size="small">
                            <EditIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {templates.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info">{t('remarketing.no_templates')}</Alert>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Tab 2: Audience */}
          {tab === 2 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('remarketing.audience_list')}</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t('remarketing.audience_type')}</InputLabel>
                    <Select
                      value={audienceType}
                      label={t('remarketing.audience_type')}
                      onChange={(e) => setAudienceType(e.target.value)}
                    >
                      <MenuItem value="abandoned_cart">{t('remarketing.audience_abandoned_cart')}</MenuItem>
                      <MenuItem value="inactive">{t('remarketing.audience_inactive')}</MenuItem>
                      <MenuItem value="high_value">{t('remarketing.audience_high_value')}</MenuItem>
                      <MenuItem value="recent_visitors">{t('remarketing.audience_recent_visitors')}</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={<DownloadIcon />}>
                    {t('remarketing.export_list')}
                  </Button>
                </Box>
              </Box>

              {/* Audience Stats */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {audience.stats?.total || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('remarketing.stat_matched')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {audience.stats?.with_phone || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('remarketing.stat_with_phone')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" color="info.main">
                      {audience.stats?.with_email || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('remarketing.stat_with_email')}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                      ${audience.stats?.total_value?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('remarketing.stat_potential_value')}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Session ID</TableCell>
                      <TableCell>{t('remarketing.col_phone')}</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>{t('remarketing.col_cart_value')}</TableCell>
                      <TableCell>{t('remarketing.col_last_viewed')}</TableCell>
                      <TableCell>{t('remarketing.col_visit_count')}</TableCell>
                      <TableCell>{t('remarketing.col_last_visit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audience.items?.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {item.session_id?.substring(0, 16)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.user_phone ? (
                            <Chip size="small" label={item.user_phone} color="success" variant="outlined" />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {item.user_email ? (
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {item.user_email}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {item.cart_value > 0 ? (
                            <Typography color="error" fontWeight="bold">
                              ${item.cart_value.toLocaleString()}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {item.last_product_name ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.last_product_image && (
                                <Box
                                  component="img"
                                  src={item.last_product_image}
                                  alt=""
                                  sx={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 1 }}
                                />
                              )}
                              <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                                {item.last_product_name}
                              </Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{item.visit_count || 1}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDateTime(item.last_visit_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!audience.items || audience.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">{t('remarketing.no_audience')}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tab 3: Reports */}
          {tab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('remarketing.time_range')}</InputLabel>
                    <Select
                      value={reportDays}
                      label={t('remarketing.time_range')}
                      onChange={(e) => setReportDays(e.target.value)}
                    >
                      <MenuItem value={7}>{t('remarketing.last_7_days')}</MenuItem>
                      <MenuItem value={30}>{t('remarketing.last_30_days')}</MenuItem>
                      <MenuItem value={90}>{t('remarketing.last_90_days')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Campaign Performance Chart */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('remarketing.performance_trend')}</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reports.campaigns}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="total_sent" name={t('remarketing.chart_sent')} fill="#8884d8" />
                      <Bar dataKey="total_opened" name={t('remarketing.chart_opened')} fill="#82ca9d" />
                      <Bar dataKey="total_clicked" name={t('remarketing.chart_clicked')} fill="#ffc658" />
                      <Bar dataKey="total_converted" name={t('remarketing.chart_converted')} fill="#ff7300" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Channel Distribution */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('remarketing.channel_distribution')}</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={reports.channels}
                        dataKey="total_sent"
                        nameKey="channel"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ channel, percent }) =>
                          `${CHANNEL_CONFIG[channel]?.label || channel} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {reports.channels.map((entry, index) => (
                          <Cell key={index} fill={CHANNEL_CONFIG[entry.channel]?.color || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Detailed Report Table */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>{t('remarketing.detailed_report')}</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell>{t('remarketing.col_campaign_name')}</TableCell>
                          <TableCell>{t('remarketing.col_type')}</TableCell>
                          <TableCell align="right">{t('remarketing.chart_sent')}</TableCell>
                          <TableCell align="right">{t('remarketing.chart_opened')}</TableCell>
                          <TableCell align="right">{t('remarketing.col_open_rate')}</TableCell>
                          <TableCell align="right">{t('remarketing.chart_clicked')}</TableCell>
                          <TableCell align="right">{t('remarketing.col_click_rate')}</TableCell>
                          <TableCell align="right">{t('remarketing.chart_converted')}</TableCell>
                          <TableCell align="right">{t('remarketing.col_conversion_rate')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reports.campaigns.map((campaign) => {
                          const openRate = campaign.total_sent > 0 ? (campaign.total_opened / campaign.total_sent * 100).toFixed(1) : 0;
                          const clickRate = campaign.total_opened > 0 ? (campaign.total_clicked / campaign.total_opened * 100).toFixed(1) : 0;
                          const convRate = campaign.total_sent > 0 ? (campaign.total_converted / campaign.total_sent * 100).toFixed(1) : 0;
                          return (
                            <TableRow key={campaign.id} hover>
                              <TableCell>{campaign.name}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={CAMPAIGN_TYPES[campaign.type]?.label || campaign.type}
                                  color={CAMPAIGN_TYPES[campaign.type]?.color || 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">{campaign.total_sent}</TableCell>
                              <TableCell align="right">{campaign.total_opened}</TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={`${openRate}%`} color={openRate > 20 ? 'success' : 'default'} />
                              </TableCell>
                              <TableCell align="right">{campaign.total_clicked}</TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={`${clickRate}%`} color={clickRate > 5 ? 'success' : 'default'} />
                              </TableCell>
                              <TableCell align="right">{campaign.total_converted}</TableCell>
                              <TableCell align="right">
                                <Chip size="small" label={`${convRate}%`} color={convRate > 2 ? 'success' : 'default'} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {reports.campaigns.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">{t('remarketing.no_report_data')}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Campaign Create/Edit Dialog */}
      <Dialog
        open={campaignDialog.open}
        onClose={() => setCampaignDialog({ open: false, data: null, mode: 'create' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {campaignDialog.mode === 'create' ? t('remarketing.create_campaign') : t('remarketing.edit_campaign')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('remarketing.campaign_name')}
                value={campaignDialog.data?.name || ''}
                onChange={(e) => setCampaignDialog(prev => ({
                  ...prev,
                  data: { ...prev.data, name: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>{t('remarketing.campaign_type')}</InputLabel>
                <Select
                  value={campaignDialog.data?.type || 'promotion'}
                  label={t('remarketing.campaign_type')}
                  onChange={(e) => setCampaignDialog(prev => ({
                    ...prev,
                    data: { ...prev.data, type: e.target.value }
                  }))}
                >
                  {Object.entries(CAMPAIGN_TYPES).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {val.icon} {val.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>{t('remarketing.message_template')}</InputLabel>
                <Select
                  value={campaignDialog.data?.template_id || ''}
                  label={t('remarketing.message_template')}
                  onChange={(e) => setCampaignDialog(prev => ({
                    ...prev,
                    data: { ...prev.data, template_id: e.target.value }
                  }))}
                >
                  <MenuItem value="">{t('remarketing.no_template')}</MenuItem>
                  {templates.map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>{tpl.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('remarketing.send_channels')}</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {Object.entries(CHANNEL_CONFIG).map(([key, val]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={campaignDialog.data?.channels?.includes(key) || false}
                        onChange={(e) => {
                          const channels = campaignDialog.data?.channels || [];
                          const newChannels = e.target.checked
                            ? [...channels, key]
                            : channels.filter(c => c !== key);
                          setCampaignDialog(prev => ({
                            ...prev,
                            data: { ...prev.data, channels: newChannels }
                          }));
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {val.icon} {val.label}
                      </Box>
                    }
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={campaignDialog.data?.is_automated || false}
                    onChange={(e) => setCampaignDialog(prev => ({
                      ...prev,
                      data: { ...prev.data, is_automated: e.target.checked }
                    }))}
                  />
                }
                label={t('remarketing.enable_automation')}
              />
              {campaignDialog.data?.is_automated && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('remarketing.automation_hint')}
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialog({ open: false, data: null, mode: 'create' })}>
            {t('remarketing.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveCampaign}>
            {t('remarketing.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog
        open={sendDialog.open}
        onClose={() => !sendDialog.sending && setSendDialog({ open: false, campaign: null, sending: false })}
      >
        <DialogTitle>{t('remarketing.confirm_send')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('remarketing.confirm_send_message', { name: sendDialog.campaign?.name })}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('remarketing.send_via', { channels: sendDialog.campaign?.channels?.map(c => CHANNEL_CONFIG[c]?.label).join(', ') })}
          </Typography>
          {sendDialog.sending && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('remarketing.sending')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSendDialog({ open: false, campaign: null, sending: false })}
            disabled={sendDialog.sending}
          >
            {t('remarketing.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendCampaign}
            disabled={sendDialog.sending}
            startIcon={<SendIcon />}
          >
            {t('remarketing.confirm_send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, content: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('remarketing.message_preview')}</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
            {previewDialog.content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, content: '' })}>
            {t('remarketing.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRemarketing;
